<template>
  <div>
    <b-form-file
      v-model="newFile"
      :accept="acceptedFileTypes"
      :state="newFileState"
      placeholder="Eine Datei auswählen oder ablegen..."
      drop-placeholder="Datei hier reinziehen..."></b-form-file>
    <b-button @click="newFileUpload()" class="mt-2" v-if="newFile">
      <b-spinner
        v-if="newFileInProgress"
        small
        label="Small Spinner"
        variant="light"
        class="mr-2"></b-spinner>
      Importieren
    </b-button>
    <div v-if="finished" class="text-success my-3"><font-awesome-icon :icon="['fas', 'check-circle']" />&nbsp;Datei eingelesen</div>
  </div>
</template>

<script>
  import papa from "papaparse";

  export default {
  name: "FileUploadComponent",
    props: {
      reduceTo: {
        type: Object,
        optional: false,
      },
      fileType: {
        type: String,
        optional: false,
      },
      acceptedFileTypes: {
        type: String,
        default: ".txt",
      },
      fileFormat: {
        type: Object,
        default: () => ({
          skipLines: 0,
          delimiter: "",
          sheetName: "",
        }),
      },
    },
    data() {
      return {
        newFile: null,
        newFileState: null,
        newFileInProgress: false,
        fileReader: new FileReader(),
        finished: false,
      };
    },
    methods: {
      chunkArray(array, chunkSize) {
        const chunkedArray = [];
        while (array.length) {
          chunkedArray.push(array.splice(0, chunkSize));
        }
        return chunkedArray;
      },
      parse(rawData) {
        return new Promise((resolve, reject) => {
          let dataToParse = rawData;
          if (this.fileFormat.skipLines > 0) {
            dataToParse = this.cleanData(rawData, this.fileFormat.skipLines);
          }

          papa.parse(dataToParse, {
            header: true,
            skipEmptyLines: true,
            delimiter: this.fileFormat.delimiter,
            complete: (result) => {
              resolve({
                rows: result.data || [],
                headers: result.meta?.fields || [],
              });
            },
            error: (error) => {
              reject(error);
            },
          });
        });
      },
      extractHeadersFromRows(rows) {
        const headerSet = new Set();

        rows.forEach((row) => {
          if (!row || typeof row !== "object") {
            return;
          }

          Object.keys(row).forEach((header) => {
            if (typeof header === "string" && header.trim() !== "") {
              headerSet.add(header);
            }
          });
        });

        return Array.from(headerSet);
      },
      cleanData(data, toRemove) {
        let arr = data.split(/\r\n|\n|\r/);
        arr.splice(0, toRemove);
        return arr.join("\n");
      },
      reduceDataNew(arr, reduceTo) {
        const ret = [];
        for (const el of arr) {
          const line = {};
          Object.keys(reduceTo).forEach((key) => {
            line[key] = el[reduceTo[key]];
          });
          ret.push(line);
        }
        return ret;
      },
      async newFileUpload() {
        this.newFileInProgress = true;
        this.fileReader.onload = async (e) => {
          try {
            const acceptedFileTypesArr = this.acceptedFileTypes.split(",");

            let parsedRows = [];
            let detectedHeaders = [];

            if (acceptedFileTypesArr.some((type) => type.trim() === ".xlsx")) {
              const XLSX = require("xlsx");
              const data = new Uint8Array(e.target.result);
              const workbook = XLSX.read(data, { type: "array" });
              const sheetName = this.fileFormat.sheetName || workbook.SheetNames[0];
              const worksheet = workbook.Sheets[sheetName];

              if (!worksheet) {
                throw new Error(`Das Tabellenblatt '${sheetName}' wurde nicht gefunden.`);
              }

              // Skip n rows
              const n = this.fileFormat.skipLines;
              if (worksheet["!ref"]) {
                const range = XLSX.utils.decode_range(worksheet["!ref"]);
                range.s.r += n;
                if (range.s.r >= range.e.r) range.s.r = range.e.r;
                worksheet["!ref"] = XLSX.utils.encode_range(range);
              }

              parsedRows = XLSX.utils.sheet_to_json(worksheet, {defval: ""});
              detectedHeaders = this.extractHeadersFromRows(parsedRows);
            } else {
              const parsedResult = await this.parse(e.target.result);
              parsedRows = parsedResult.rows;
              detectedHeaders = parsedResult.headers;
            }

            if (!Array.isArray(parsedRows) || parsedRows.length === 0) {
              this.newFileInProgress = false;
              this.$emit("file-error", "Die Datei enthält keine Datensätze.");
              return;
            }

            if (!Array.isArray(detectedHeaders) || detectedHeaders.length === 0) {
              detectedHeaders = this.extractHeadersFromRows(parsedRows);
            }

            this.$emit("file-parsed", {
              parsedRows,
              detectedHeaders,
              fileType: this.fileType,
            });

            this.newFileInProgress = false;
            this.newFile = null;
            this.finished = true;
            setTimeout(() => {
              this.finished = false;
            }, 3000);
          } catch (error) {
            this.newFileInProgress = false;
            this.$emit("file-error", error.message || "Die Datei konnte nicht verarbeitet werden.");
          }
        };
        this.fileReader.onerror = () => {
          this.newFileInProgress = false;
          this.$emit("file-error", "Die Datei konnte nicht gelesen werden.");
        };
        if (this.acceptedFileTypes.split(",").some((type) => type.trim() === ".xlsx")) {
          this.fileReader.readAsArrayBuffer(this.newFile);
        } else {
          this.fileReader.readAsText(this.newFile, "UTF-8");
        }
      },
    },
  };
</script>
